import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { NotificationType, TrackingEvent, StyleguideModule } from '@woolworthsnz/styleguide';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { buildDirectionsUrl } from '../../helpers';
import { NgIf } from '@angular/common';
import { PinWithNumberComponent } from '../pin-with-number/pin-with-number.component';

dayjs.extend(isSameOrAfter);

@Component({
	selector: 'store-locator-search-summary',
	template: `
		<store-locator-pin-with-number [fill]="isOpen ? 'green' : 'alert'" [label]="rank"></store-locator-pin-with-number>
		<article>
			<header class="summaryHeader">
				<h3
					(click)="detailsClick.emit()"
					[cdxTrackEvent]="{
						event: trackingEvent.NotificationEvent,
						type: notificationType.Link,
						name: 'store_details',
						value: id
					}"
					class="summaryHeader-title"
				>
					{{ name }}
				</h3>
				<p class="summaryHeader-distance">{{ distance }} km</p>
			</header>
			<div class="summaryBody">
				<address>
					{{ street }}<br />
					{{ suburb }}<br />
					{{ state }} {{ postcode }}
				</address>
				<ng-container *ngIf="isOpen; else closed">
					<p i18n="@@storeSummary-open"><strong>Open</strong> Closes at {{ closingTime | formattedDate: 'time' }}</p>
				</ng-container>
				<ng-template #closed>
					<p i18n="@@storeSummary-closed">
						<strong class="summaryBody-openClosed--red">Closed</strong>
						<span *ngIf="expectedOpeningTime">
							Opens
							{{ expectedOpeningTime | formattedDate: 'dayOfWeekWithDate':{ sameDay: 'time' } }}
						</span>
					</p>
				</ng-template>
				<div class="summaryBody-actions">
					<button
						cdxButton
						fillStyle="secondaryOutline"
						target="_blank"
						i18n="@@storeSummary-directions"
						[cdxTrackEvent]="{
							event: trackingEvent.NotificationEvent,
							type: notificationType.Button,
							name: 'directions',
							value: id
						}"
						(click)="directionsClick.emit(directionsUrl)"
					>
						Directions
					</button>
					<button
						cdxButton
						fillStyle="secondaryOutline"
						i18n="@@storeSummary-storeDetails"
						(click)="detailsClick.emit()"
						[cdxTrackEvent]="{
							event: trackingEvent.NotificationEvent,
							type: notificationType.Button,
							name: 'store_details',
							value: id
						}"
					>
						Store details
					</button>
				</div>
			</div>
		</article>
	`,
	styleUrls: ['./search-summary.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [PinWithNumberComponent, StyleguideModule, NgIf]
})
export class SearchSummaryComponent {
	@Input() id: number;
	@Input() rank: number;
	@Input() name: string;
	@Input() distance: number;
	@Input() street: string;
	@Input() state: string;
	@Input() suburb: string;
	@Input() postcode: string;
	@Input() openingTime: Date;
	@Input() closingTime: Date;
	@Input() nextOpeningTime?: Date;
	@Input() latitude: number;
	@Input() longitude: number;

	@Output() detailsClick = new EventEmitter();
	@Output() directionsClick = new EventEmitter<string>();

	notificationType = NotificationType;
	trackingEvent = TrackingEvent;

	get isOpen(): boolean {
		// TODO: CO-9550 remove dayjs() and use server time offset
		const now = dayjs();
		return now.isSameOrAfter(this.openingTime) && now.isBefore(this.closingTime);
	}

	get directionsUrl(): string {
		return buildDirectionsUrl(this.latitude, this.longitude);
	}

	get expectedOpeningTime(): Date | undefined {
		// if the store will be open later today return the opening time
		if (dayjs().isBefore(this.openingTime) && dayjs(this.openingTime).isBefore(this.closingTime)) {
			return this.openingTime;
		}

		return this.nextOpeningTime;
	}
}
